import { memone } from '@deqo/foundation';
import { shade } from 'polished';
import React, { FC, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { shadeSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { Host } from '../../../concepts/objects/ObjectSymbols';
import { Point } from '../../../platform/geom/Point';
import { Fill } from '../../../ui/Fill';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { aspect } from '../../canvas/visualization/Aspect';
import { RenderComments, RenderTags, RenderVotes } from '../../canvas/visualization/RenderAnnotations';
import { RenderTextField } from '../../canvas/visualization/RenderField';
import { RenderBorder, RenderBounds, RenderBox, RenderCounter, RenderGraffiti, RenderHeadline, RenderInner, usePose, useUser } from '../../canvas/visualization/RenderHelpers';
import { RenderRankingMedal } from '../../canvas/visualization/RenderRankingMedal';
import { RenderText } from '../../canvas/visualization/RenderText';
import { VisualBase } from '../../canvas/visualization/VisualBase';
import { IsConcealedHintVisible } from '../conceal/Concealable';
import { VoteRankAttr } from '../voting/Vote';
import { TaskCard, } from './TaskCard';

const usePoseDerivatives = (object: TaskCard) => {
    const p = usePose(object);
    return useMemo(() => ({
        highlighted: !!(p.pressed || p.targeted || p.selected) && !p.beingManipulated,
        selected: p.selected,
        editing: p.beingEdited,
    }), [p]);
};

// const RenderHighlight = styled(Fill)`
//     border: solid 2px;
//     border-radius: 2px;
// `;

const RenderDesign = React.memo<{ color: string, highlighted: boolean, selected: boolean }>(({ color, highlighted, selected }) => {
    if (!color) return null;

    const borderColor = selected ? shadeSafe(.2, color) : color;

    // const color = this.object.autoColor || this.object.color;
    // return this.isSelected ? shade(.20, color) : color;

    return <RenderBox
        inset="0"
        background="#fff"
        borderRadius="2px"
        borderStyle="solid"
        borderWidth="2px 2px 2px 12px"
        borderColor={borderColor}
    />;
});

export const TaskCardVisual: FC<{ object: TaskCard, children?: ReactNode }> = ({ object, children }) => {

    const { highlighted, selected, editing } = usePoseDerivatives(object);
    const color = object.autoColor || object.color;
    const user = useUser(object);

    const showUser = !editing && highlighted && !!user && !IsConcealedHintVisible.get(object);

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderDesign {...{ color, highlighted, selected }} />
                {/* <RenderBox
                    left="20px"
                    right="10px"
                    top="40px"
                    height="1px"
                    background="#888"
                /> */}
                <RenderGraffiti object={object} />
                {!editing && <RenderText {...object.layout.title} value={object.title} formatOpts={['mention', 'bold', 'italic']} />}
                {!editing && <RenderText {...object.layout.content} value={object.content} formatOpts={['mention', 'bold', 'italic', 'list']} />}
                <RenderComments object={object} />
                <RenderVotes object={object} />
                <RenderTags object={object} />
                <RenderRankingMedal value={VoteRankAttr.get(object)} show={!highlighted} />
                {showUser &&
                    <RenderHeadline>
                        <RenderCounter user={user} />
                    </RenderHeadline>
                }
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};

/* eslint-disable max-len */
/* eslint-disable no-mixed-operators */
export class TaskCardVisualOld extends VisualBase<TaskCard> {

    @aspect()
    private get fillColor() {
        // Important we read both!
        const color = this.object.autoColor || this.object.color;
        return this.isSelected ? shade(.20, color) : color;
    }

    @aspect()
    private get shadow() {
        // Compute shadow angle for rotation
        const pt = new Point(0, 3).rotate(this.object.r * -1);
        return `${pt.x}px ${pt.y}px 10px 0 rgba(0, 0, 0, 0.1)`;
    }

    @aspect()
    private get borderStyling(): any {
        const { object } = this;
        return {
            width: object.width,
            height: object.height,
            backgroundColor: this.fillColor,
            borderRadius: '2px',
            boxShadow: this.lgfx ? null : this.shadow,
        };
    }

    @aspect()
    private get innerStyling(): any {

        return {
        };
    }

    @aspect()
    private get highlightStyling(): any {
        const color = this.object.autoColor || this.object.color;
        return { borderColor: shade(.2, color) };
    }

    private get user() {
        return this.resolveUser(this.object.userId);
    }

    public render() {
        return <RenderBounds object={this.object}>
            <RenderBorder pose={this.pose} style={this.borderStyling}>
                <RenderGraffiti object={this.object} />
                {this.isHighlighted && <RenderHighlight style={this.highlightStyling} />}
                {!this.isBeingEdited && <>
                    <RenderTextField object={this.object} name="title" />
                    <RenderTextField object={this.object} name="content" />
                </>}
                <RenderComments object={this.object} />
                <RenderVotes object={this.object} />
                <RenderTags object={this.object} />
                <RenderRankingMedal value={VoteRankAttr.get(this.object)} show={!this.isHighlighted} />
            </RenderBorder>
            {this.isHighlighted && !this.isBeingEdited && this.user &&
                <RenderHeadline>
                    <RenderCounter user={this.user} />
                </RenderHeadline>
            }
        </RenderBounds>;
    }

    @memone()
    private resolveUser(id: string) {
        if (!id) return null;
        return this.props.object[Host].resolve(ParticipantManager).get(id);
    }
}

const RenderHighlight = styled(Fill)`
    border: solid 2px;
    border-radius: 2px;
`;
